/**
 * <s-share-button> — Shares the current product with the native share sheet,
 * or copies the link to the clipboard when sharing is unavailable.
 *
 * Commands (via commandfor/command on a descendant button):
 *   --share — Open the share sheet or copy the link.
 *
 * Attributes:
 *   data-share-url    — Absolute URL to share.
 *   data-share-title  — Title passed to the share sheet.
 *   data-copied-text  — Translated feedback shown after copying.
 *
 * Child markup:
 *   [data-share-label] — Text replaced briefly with the copied feedback.
 */
class SShareButton extends HTMLElement {
  #ac;
  #resetTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command === '--share') this.#share();
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#resetTimer);
  }

  async #share() {
    const url = this.dataset.shareUrl;
    const title = this.dataset.shareTitle;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // The visitor closed the share sheet.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.#showCopied();
    } catch {
      // Clipboard access was denied; nothing else to do.
    }
  }

  #showCopied() {
    const label = this.querySelector('[data-share-label]');
    const copiedText = this.dataset.copiedText;
    if (!label || !copiedText) return;
    const original = label.dataset.originalText || label.textContent;
    label.dataset.originalText = original;
    label.textContent = copiedText;
    const announcer = document.getElementById('sr-announcer');
    if (announcer) announcer.textContent = copiedText;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
}

if (!customElements.get('s-share-button')) customElements.define('s-share-button', SShareButton);
